import {
  actualizarEncuestaPregunta,
  crearEncuestaPregunta,
  eliminarEncuestaPregunta,
  getEncuestaPreguntaPorField,
  getEncuestaPreguntas,
} from 'api/encuestaPregunta'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import swal from 'sweetalert2'

const key = 'EncuestaPreguntas'
export function useObtenerEncuestaPregunta(buscar) {
  return useQuery(key, () => getEncuestaPreguntas(buscar))
}

export function useObtenerEncuestaPreguntaPorField(field, id) {
  return useQuery(key, () => getEncuestaPreguntaPorField(field, id))
}

export function useCrearEncuestaPregunta() {
  const queryClient = useQueryClient()
  return useMutation(crearEncuestaPregunta, {
    onSuccess: (response) => {
      const { data } = response
      queryClient.setQueryData(key, (prevState) => {
        const results = [data, ...prevState.results]
        return { ...prevState, results }
      })
    },
  })
}

export function useActualizarEncuestaPregunta() {
  const queryClient = useQueryClient()
  return useMutation(actualizarEncuestaPregunta, {
    onSuccess: (_, value) => {
      const { id, values: EncuestaPreguntaActualizada } = value
      queryClient.setQueryData(key, (prevState) => {
        const results = prevState.results.map((EncuestaPregunta) => {
          let nuevoEncuestaPregunta = EncuestaPregunta
          if (id === EncuestaPregunta.id) {
            nuevoEncuestaPregunta = EncuestaPreguntaActualizada
          }
          return nuevoEncuestaPregunta
        })
        return { ...prevState, results }
      })
    },
  })
}

export function useEliminarEncuestaPregunta() {
  const queryClient = useQueryClient()
  return useMutation(eliminarEncuestaPregunta, {
    onSuccess: (_, value) => {
      queryClient.setQueryData(key, (prevState) => {
        const results = prevState.results.filter(
          (EncuestaPregunta) => EncuestaPregunta.id !== value.id,
        )
        return { ...prevState, results }
      })
    },
  })
}

export function useEncuestaPregunta(onToggle) {
  const queryClient = useQueryClient()
  const { mutateAsync: eliminarEncuestaPregunta } = useEliminarEncuestaPregunta()
  const { mutateAsync: actualizarEncuestaPregunta } = useActualizarEncuestaPregunta()
  const { mutateAsync: crearEncuestaPregunta } = useCrearEncuestaPregunta()

  const renderErrorMessage = (error) => {
    swal
      .fire({
        ...error,
        type: 'error',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Continuar',
      })
  }

  const handleCrearEncuestaPregunta = async (values) => {
    try {
      await crearEncuestaPregunta(values)
    } catch (error) {
      renderErrorMessage({
        title: 'Error',
        text: Object.values(error)[0],
        icon: 'error',
      })
    }
  }

  const handleActualizarEncuestaPregunta = async ({ id, values, acciones }) => {
    try {
      const { data, onSuccessMessage } = await actualizarEncuestaPregunta({
        id,
        values,
      })
      if (data) {
        onSuccessMessage({
          text: 'La EncuestaPregunta fue actualizada satisfactoriamente!',
        })
        onToggle()
      }
    } catch (error) {
      renderErrorMessage({
        title: 'Error',
        text: Object.values(error)[0],
        icon: 'error',
      })
    }
  }

  const handleEliminarEncuestaPregunta = (id) => {
    swal
      .fire({
        title: 'Estás seguro?',
        text: 'Recuerda que no podrás revertir estos cambios!',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si, eliminalo!',
      })
      .then(async ({ value }) => {
        if (value) {
          const { data, onSuccessMessage } = await eliminarEncuestaPregunta(id)
          const message = `La EncuestaPregunta ${data} fue eliminada satisfactoriamente!`
          onSuccessMessage({ text: message })
        }
      })
  }

  const handleBuscarEncuestaPregunta = async (value) => {
    queryClient.prefetchQuery(key, () => getEncuestaPreguntas(value))
  }

  return {
    obtenerEncuestaPreguntas: useObtenerEncuestaPregunta,
    handleEliminarEncuestaPregunta,
    handleActualizarEncuestaPregunta,
    handleCrearEncuestaPregunta,
    handleBuscarEncuestaPregunta,
    useObtenerEncuestaPreguntaPorField: useObtenerEncuestaPreguntaPorField
  }
}
